import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import {
  Avatar,
  Box,
  Divider,
  Paper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import toast from 'react-hot-toast';
import axiosInstant from '../../../api';
import {
  adminProductBestMatches,
  adminCategoryBestMatches,
  adminUrlBestMatches,
} from '../../../api/endpoints';
import { DotLottleLoader } from '../../../components/Loader';
import { Button } from '../../../components/Button/Button';
import { MATH_FONT, Var, Op, pct } from './equationPrimitives';
import { AffinityBands, bandFor, gradeColor } from './bands';
import {
  TIER_COLORS,
  PICKER_INPUT_SX,
  DEMO_SX,
  DEMO_COPY,
  DemoMode,
  DEFAULT_PRODUCT,
  messageFor,
  overlapColor,
  dotSx,
} from './AffinityNetwork/affinity.constants';
import { ProductPicker } from './AffinityNetwork/ProductPicker';
import { CategoryPicker, FocusOption } from './AffinityNetwork/CategoryPicker';
import { useAffinityCategories } from './AffinityNetwork/useAffinityGraph';
import imagePlaceholder from '../../../assets/svg/image_placeholder.svg';
import { cldUrl } from '../../../utils/cloudinary';

interface ProductCategoryInfo {
  id: number;
  name: string;
  tier: string | null;
}

interface ProductInfo {
  desireid: number | null;
  title: string | null;
  image: string | null;
  categories: ProductCategoryInfo[];
  live?: boolean;
  url?: string | null;
  brand?: string | null;
  classification?: { reason: string } | null;
}

interface AttributeOverlap {
  attrName: string | null;
  attrSlug: string | null;
  weight: number;
  overlap: number;
  universal: boolean;
  expertValues: string[];
  productValues: string[];
  shared: string[];
}

interface ProfessionInfo {
  professionName: string | null;
  subProfessionName: string | null;
  areaOfFocusName: string | null;
  years: number;
  isActive: boolean;
}

interface AffinityMatch {
  categoryName: string;
  grade: number;
  rung: string;
}

interface ExpertTypeInfo {
  type: 'industry' | 'use-case';
  signals?: {
    relevantProfessions: number;
    professionalTier: boolean;
    regulated: boolean;
    bestTenureYears: number;
    minYears: number;
    qualifyingProfessions: Array<{
      name: string | null;
      years: number;
      professionalTier: boolean;
      regulated: boolean;
    }>;
  };
}

interface ExpertRow {
  user_id: number;
  score: number;
  attribute_alignment: number; // A
  profession_contribution: number; // Q
  category_affinity: number; // α
  computed_at: string | null;
  expert: {
    userid: number;
    displayName: string | null;
    avatar: string | null;
    verified?: boolean;
    usertype?: string | null;
  } | null;
  professions: ProfessionInfo[];
  affinityMatches: AffinityMatch[];
  expertType?: ExpertTypeInfo | null;
  overlaps: AttributeOverlap[];
}

interface Props {
  affinityBands: AffinityBands;
}

function useBestMatches(mode: DemoMode, key: number | string | null, limit: number) {
  const [product, setProduct] = useState<ProductInfo | null>(null);
  const [rows, setRows] = useState<ExpertRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const latestRequest = useRef(0);

  const refetch = useCallback(async () => {
    const requestId = latestRequest.current + 1;
    latestRequest.current = requestId;
    const isStale = () => latestRequest.current !== requestId;

    if (!key) {
      setRows([]);
      setProduct(null);
      setError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = mode === 'url'
        ? await axiosInstant.post(adminUrlBestMatches, { url: key, limit })
        : await axiosInstant.get(
          mode === 'product'
            ? adminProductBestMatches(Number(key), limit)
            : adminCategoryBestMatches(Number(key), limit),
        );
      if (isStale()) return;
      const data = res.data || {};
      setProduct(data.product ?? null);
      setRows(Array.isArray(data.experts) ? data.experts : []);
    } catch (err) {
      if (isStale()) return;
      const message = messageFor(err, mode);
      setError(message);
      setRows([]);
      setProduct(null);
      toast.error(message);
    } finally {
      if (!isStale()) setLoading(false);
    }
  }, [mode, key, limit]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { product, rows, loading, error, refetch };
}

const ScoreChip: FC<{ value: number; color: string; big?: boolean }> = ({ value, color, big }) => (
  <Box
    sx={{
      bgcolor: color,
      color: '#fff',
      px: big ? 1.5 : 1,
      py: big ? 0.5 : 0.25,
      borderRadius: 1,
      fontSize: big ? 18 : 12,
      fontWeight: 700,
      lineHeight: 1.3,
      flexShrink: 0,
    }}
  >
    {pct(value)}
  </Box>
);

// Industry/Use-Case pill — the binary twin of the Industry lens. Tooltip
// explains WHY via the classification signals (qualifying professions, tier,
// regulation, tenure) so the demo stays a worked example.
const ExpertTypePill: FC<{ expertType?: ExpertTypeInfo | null }> = ({ expertType }) => {
  if (!expertType) return null;
  const isIndustry = expertType.type === 'industry';
  const s = expertType.signals;
  let why = '';
  if (isIndustry) {
    const quals = (s?.qualifyingProfessions || []).slice(0, 3).map((q) => {
      const tags = [q.professionalTier ? 'professional-tier' : null, q.regulated ? 'regulated' : null]
        .filter(Boolean).join(', ');
      return `${q.name || 'matching profession'} (${q.years} yrs${tags ? `, ${tags}` : ''})`;
    });
    why = `Industry Expert — credentialed professional in this product's field. Qualifies via ${quals.join('; ') || 'a professional-grade relevant profession'}.`;
  } else if (s) {
    if (s.relevantProfessions === 0) {
      why = 'Use-Case Expert — no profession with category affinity to this product.';
    } else if (!s.professionalTier && !s.regulated) {
      why = 'Use-Case Expert — matching professions are enthusiast/consumer-tier, not professional-tier or regulated.';
    } else if (s.bestTenureYears < s.minYears) {
      why = `Use-Case Expert — professional-tier field but tenure (${s.bestTenureYears} yrs) is under the ${s.minYears}-year bar.`;
    } else {
      why = 'Use-Case Expert — credible practitioner rather than credentialed professional.';
    }
  }
  return (
    <Box
      component="span"
      title={why}
      sx={{
        px: 0.75,
        py: '1px',
        borderRadius: 1,
        bgcolor: isIndustry ? '#E4EDF6' : '#F2EEE8',
        color: isIndustry ? '#4A7BA6' : '#9B958C',
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '0.5px',
        textTransform: 'uppercase',
        flexShrink: 0,
        cursor: 'help',
      }}
    >
      {isIndustry ? 'Industry' : 'Use-Case'}
    </Box>
  );
};

const ExpertAvatar: FC<{ row: ExpertRow; size: number }> = ({ row, size }) => (
  <Avatar src={row.expert?.avatar ? cldUrl(row.expert.avatar, { w: size * 2, h: size * 2, crop: "fill", gravity: "face" }) : undefined} sx={{ width: size, height: size, flexShrink: 0 }}>
    {(row.expert?.displayName || '?').charAt(0)}
  </Avatar>
);

const Name: FC<{ row: ExpertRow; bold?: boolean }> = ({ row, bold }) => (
  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
    <Typography component="span" sx={{ fontWeight: bold ? 700 : 500, fontSize: bold ? 16 : 14 }} noWrap>
      {row.expert?.displayName || `User ${row.user_id}`}
    </Typography>
    {row.expert?.verified && <CheckCircleIcon sx={{ fontSize: bold ? 18 : 15, color: '#1976d2' }} />}
  </Box>
);

// One attribute's contribution to A: name, weight, overlap bar, and the values.
const OverlapRow: FC<{ o: AttributeOverlap }> = ({ o }) => (
  <Box sx={{ py: 0.75 }}>
    <Box className="flex items-center gap-2">
      <Typography sx={{ fontSize: 13, fontWeight: 600, width: 150, flexShrink: 0 }} noWrap>
        {o.attrName || o.attrSlug || 'Attribute'}
      </Typography>
      <Box sx={{ flex: 1, height: 8, borderRadius: 4, bgcolor: 'var(--track)', overflow: 'hidden', minWidth: 60 }}>
        <Box sx={{ width: `${Math.round(o.overlap * 100)}%`, height: '100%', bgcolor: overlapColor(o.overlap, o.universal) }} />
      </Box>
      <Typography sx={{ fontSize: 12, color: 'var(--text-2)', width: 86, textAlign: 'right', flexShrink: 0 }}>
        {o.universal ? 'universal' : pct(o.overlap)}
      </Typography>
    </Box>
    <Typography sx={{ fontSize: 11.5, color: 'var(--text-2)', pl: '158px' }}>
      {o.shared.length > 0 && (
        <Box component="span" sx={{ color: '#16A34A' }}>shared: {o.shared.join(', ')}{'  ·  '}</Box>
      )}
      expert: {o.expertValues.join(', ') || '—'}
      {'  ·  '}
      product: {o.productValues.join(', ') || '—'}
    </Typography>
  </Box>
);

const OverlapList: FC<{ overlaps?: AttributeOverlap[] }> = ({ overlaps }) => {
  const list = overlaps ?? [];
  return (
    <Box>
      {list.length === 0 && (
        <Typography sx={DEMO_SX.mutedText}>
          This product carries no weighted attributes to compare.
        </Typography>
      )}
      {list.map((o, i) => (
        <OverlapRow key={o.attrSlug || o.attrName || `overlap-${i}`} o={o} />
      ))}
    </Box>
  );
};

// Professions, areas of focus and tenure — the source of both affinity and Q.
const ProfessionsSection: FC<{ professions?: ProfessionInfo[] }> = ({ professions }) => {
  const list = professions ?? [];
  if (list.length === 0) {
    return <Typography sx={DEMO_SX.mutedText}>No professions on file.</Typography>;
  }
  return (
    <Box className="flex flex-col gap-1">
      {list.map((p, i) => (
        <Box key={i} className="flex items-center gap-2 flex-wrap">
          <Typography sx={DEMO_SX.rowLabel}>
            {p.subProfessionName || p.professionName || 'Profession'}
          </Typography>
          {p.professionName && p.subProfessionName && (
            <Typography sx={DEMO_SX.mutedText}>in {p.professionName}</Typography>
          )}
          {p.areaOfFocusName && (
            <Box
              component="span"
              title="Area of focus"
              sx={{ px: 0.75, py: '1px', borderRadius: 1, bgcolor: '#EEF2FF', color: '#4F46E5', fontSize: 11, fontWeight: 600 }}
            >
              {p.areaOfFocusName}
            </Box>
          )}
          <Typography sx={DEMO_SX.subtleText}>
            · {p.years.toFixed(1)} yrs{p.isActive ? '' : ' (past)'}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

// Which of the product's categories this expert works in, and at what rung — the
// affinity "why" (the α that picks the band).
const AffinitySection: FC<{ matches?: AffinityMatch[] }> = ({ matches }) => {
  const list = matches ?? [];
  if (list.length === 0) {
    return (
      <Typography sx={DEMO_SX.mutedText}>
        No category affinity — surfaced on attributes + experience only.
      </Typography>
    );
  }
  return (
    <Box className="flex flex-col gap-1">
      {list.map((m, i) => (
        <Box key={i} className="flex items-center gap-2">
          <Box sx={dotSx(9, gradeColor(m.grade))} />
          <Typography sx={DEMO_SX.rowLabel}>{m.categoryName}</Typography>
          <Typography sx={DEMO_SX.subtleText}>{m.rung} · α = {m.grade}</Typography>
        </Box>
      ))}
    </Box>
  );
};

// The three "why" sections for an expert: affinity (band), professions (Q),
// attributes (A). Shared by the hero and the expandable ranked rows.
const ExpertDetail: FC<{ row: ExpertRow }> = ({ row }) => (
  <>
    <Divider sx={{ my: 1.5 }} />
    <Typography sx={DEMO_SX.sectionTitle}>Affinity — works in (picks the band)</Typography>
    <AffinitySection matches={row.affinityMatches} />
    <Divider sx={{ my: 1.5 }} />
    <Typography sx={DEMO_SX.sectionTitle}>Professions &amp; experience (drives Q)</Typography>
    <ProfessionsSection professions={row.professions} />
    <Divider sx={{ my: 1.5 }} />
    <Typography sx={DEMO_SX.sectionTitle}>Attribute overlap (drives A)</Typography>
    <OverlapList overlaps={row.overlaps} />
  </>
);

const RankedRow: FC<{ row: ExpertRow; bands: AffinityBands }> = ({ row, bands }) => {
  const [open, setOpen] = useState(false);
  const b = bandFor(row.category_affinity, bands);
  return (
    <Box sx={{ borderBottom: '1px solid var(--border)' }}>
      <Box
        className="flex items-center gap-3"
        sx={{ py: 1, cursor: 'pointer' }}
        onClick={() => setOpen((v) => !v)}
      >
        <ExpertAvatar row={row} size={32} />
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Name row={row} />
          <ExpertTypePill expertType={row.expertType} />
        </Box>
        <Typography sx={{ fontSize: 12, color: 'var(--text-2)', display: { xs: 'none', sm: 'block' } }}>
          {b.meta.label} · A {pct(row.attribute_alignment)} · Q {pct(row.profession_contribution)}
        </Typography>
        <ScoreChip value={row.score} color={b.color} />
      </Box>
      {open && (
        <Box sx={{ pb: 1.5, pl: { xs: 0, sm: 5 } }}>
          <ExpertDetail row={row} />
        </Box>
      )}
    </Box>
  );
};

const Hero: FC<{ row: ExpertRow; bands: AffinityBands }> = ({ row, bands }) => {
  const b = bandFor(row.category_affinity, bands);
  const refine = row.attribute_alignment * row.profession_contribution; // A·Q
  const reconstructed = b.band + b.width * refine;
  return (
    <Box
      sx={{
        border: `1px solid ${b.color}33`,
        borderLeft: `4px solid ${b.color}`,
        borderRadius: 1.5,
        bgcolor: 'var(--surface-2)',
        p: 2.5,
        mt: 1,
      }}
    >
      <Box className="flex items-center gap-2 flex-wrap" sx={{ mb: 1.5 }}>
        <ExpertAvatar row={row} size={56} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={DEMO_SX.inlineRow}>
            <Name row={row} bold />
            <ExpertTypePill expertType={row.expertType} />
          </Box>
          <Typography sx={DEMO_SX.subtleText} noWrap>
            Best match · {b.meta.label} ({b.meta.sublabel})
          </Typography>
        </Box>
        <ScoreChip value={row.score} color={b.color} big />
      </Box>

      {/* The equation, with this expert's real numbers. */}
      <Box sx={{ fontFamily: MATH_FONT, fontSize: { xs: 14, sm: 16 }, color: 'var(--text)', mb: 0.5 }}>
        <Var title="band(α) — the affinity floor for this expert's category match.">band</Var>
        <Op>(</Op>{b.band.toFixed(2)}<Op>)</Op>
        <Op>+</Op>
        <Var title="width(α) — the band's room for refinement.">width</Var>
        <Op>(</Op>{b.width.toFixed(2)}<Op>)</Op>
        <Op>·</Op><Op>(</Op>
        <Var title="A — attribute alignment for this expert.">A</Var> {pct(row.attribute_alignment)}
        <Op>·</Op>
        <Var title="Q — profession fit (experience-weighted) for this expert.">Q</Var> {pct(row.profession_contribution)}
        <Op>)</Op>
        <Op>=</Op>
        <Box component="strong">{pct(reconstructed)}</Box>
      </Box>
      <Typography sx={{ fontSize: 12, color: 'var(--muted)', mb: 1.5 }}>
        {row.computed_at ? 'Stored score ' : 'Score '}{pct(row.score)}
        {row.computed_at ? ` · from the last recompute (${new Date(row.computed_at).toLocaleDateString()})` : ''}
      </Typography>

      <ExpertDetail row={row} />
    </Box>
  );
};

const FrameBox: FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    className="flex flex-col items-center justify-center gap-3"
    sx={DEMO_SX.frame}
  >
    {children}
  </Box>
);

// The product being matched against: image, title, and its categories (the
// affinity drivers) as tier-coloured chips.
const ProductCard: FC<{ product: ProductInfo }> = ({ product }) => (
  <Box
    sx={{
      display: 'flex',
      gap: 2,
      alignItems: 'center',
      p: 2,
      mt: 2,
      border: '1px solid var(--border)',
      borderRadius: 1.5,
      bgcolor: 'var(--surface)',
    }}
  >
    {!(product.live && !product.image) && (
      <Box
        component="img"
        src={product.image || imagePlaceholder}
        alt={product.title || `Product ${product.desireid}`}
        onError={(e) => {
          (e.currentTarget as HTMLImageElement).src = imagePlaceholder;
        }}
        sx={DEMO_SX.thumbnail}
      />
    )}
    <Box sx={{ minWidth: 0 }}>
      <Box sx={DEMO_SX.inlineRow}>
        <Typography sx={{ fontWeight: 700 }} noWrap>
          {product.title || (product.desireid ? `Product ${product.desireid}` : 'Untitled product')}
        </Typography>
        {product.live && (
          <Box
            component="span"
            title="Scored on the spot against every expert. Nothing is stored."
            sx={DEMO_SX.liveBadge}
          >
            Scored live
          </Box>
        )}
      </Box>
      <Typography sx={DEMO_SX.mutedText}>
        {product.desireid ? `Product #${product.desireid} · ` : ''}
        {product.brand ? `${product.brand} · ` : ''}
        matched against the experts below
      </Typography>
      {product.url && (
        <Typography
          component="a"
          href={product.url}
          target="_blank"
          rel="noreferrer"
          sx={{ fontSize: 12, color: 'var(--text-2)', textDecoration: 'underline', wordBreak: 'break-all' }}
        >
          {product.url}
        </Typography>
      )}
      {product.categories.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75, mt: 1 }}>
          {product.live && (
            <Typography sx={DEMO_SX.mutedText}>Mapped to:</Typography>
          )}
          {product.categories.map((c) => (
            <Box
              key={c.id}
              sx={DEMO_SX.categoryChip}
            >
              <Box
                sx={dotSx(8, TIER_COLORS[c.tier ?? 'none'] ?? TIER_COLORS.none)}
              />
              {c.name}
            </Box>
          ))}
        </Box>
      )}
      {product.classification?.reason && (
        <Typography
          sx={DEMO_SX.reason}
        >
          {product.classification.reason}
        </Typography>
      )}
    </Box>
  </Box>
);

export const BestMatchDemo: FC<Props> = ({ affinityBands }) => {
  const [mode, setMode] = useState<DemoMode>('product');
  const [selected, setSelected] = useState<FocusOption | null>(DEFAULT_PRODUCT);
  const [category, setCategory] = useState<FocusOption | null>(null);
  const [urlInput, setUrlInput] = useState('');
  const [submittedUrl, setSubmittedUrl] = useState<string | null>(null);
  const { categories, loading: categoriesLoading, error: categoriesError } = useAffinityCategories();

  let sourceKey: number | string | null = null;
  if (mode === 'product') sourceKey = selected?.id ?? null;
  else if (mode === 'category') sourceKey = category?.id ?? null;
  else sourceKey = submittedUrl;

  const { product, rows, loading, error, refetch } = useBestMatches(mode, sourceKey, 6);

  const submitUrl = () => {
    // Enter bypasses the disabled button; each submit is a paid page fetch.
    if (loading) return;
    const trimmed = urlInput.trim() || null;
    if (trimmed === submittedUrl) {
      refetch();
      return;
    }
    setSubmittedUrl(trimmed);
  };

  const emptyPrompt = DEMO_COPY.emptyPrompts[mode];

  return (
    <Paper sx={{ p: 3 }}>
      <Box
        sx={DEMO_SX.header}
      >
        <Box sx={{ flex: '1 1 420px', minWidth: 0 }}>
          <Typography sx={DEMO_SX.eyebrow}>Worked example</Typography>
          <Typography sx={DEMO_SX.title}>Best match</Typography>
          <Typography sx={DEMO_SX.subtitle}>
            Pick a product, a category, or paste any product URL to see it matched against
            its top-scoring experts, with each score broken down into the equation above
            using real numbers, including which attributes matched.
          </Typography>
        </Box>

        <ToggleButtonGroup
          size="small"
          exclusive
          value={mode}
          onChange={(_, v) => v && setMode(v)}
          sx={DEMO_SX.segmented}
        >
          <ToggleButton value="product">By product</ToggleButton>
          <ToggleButton value="category">By category</ToggleButton>
          <ToggleButton value="url">By URL</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Divider sx={{ my: 2.5, borderColor: 'var(--border)' }} />

      <Typography sx={DEMO_SX.fieldLabel}>{DEMO_COPY.fieldLabels[mode]}</Typography>

      {mode === 'product' && (
        <Box sx={DEMO_SX.pickerWrap}>
          <ProductPicker value={selected} onChange={setSelected} />
        </Box>
      )}

      {mode === 'category' && (
        <Box sx={DEMO_SX.pickerWrap}>
          <CategoryPicker
            categories={categories}
            value={category}
            onChange={setCategory}
            label=""
            placeholder="Select Category"
            inputSx={PICKER_INPUT_SX}
            loading={categoriesLoading}
          />
          {categoriesError && (
            <Typography className="text-error" sx={{ mt: 1, fontSize: 13 }}>
              Could not load categories. Refresh to try again.
            </Typography>
          )}
        </Box>
      )}

      {mode === 'url' && (
        <Box sx={DEMO_SX.urlRow}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') submitUrl();
              }}
              placeholder="https://brand.com/products/example"
              size="small"
              sx={{ ...PICKER_INPUT_SX, ...DEMO_SX.urlField }}
            />
            <Button
              onClick={submitUrl}
              disabled={loading || !urlInput.trim()}
              className="!min-w-0 !p-0 !px-6 !h-[40px] !rounded-[12px] !text-sm"
            >
              {loading ? 'Scoring…' : 'Score'}
            </Button>
          </Box>
          {!submittedUrl && !loading && (
            <Typography sx={DEMO_SX.hint}>
              Fetched and categorised on the spot, so the first run takes up to a minute.
              Nothing is saved.
            </Typography>
          )}
        </Box>
      )}

      {!sourceKey && !loading && <FrameBox><Typography>{emptyPrompt}</Typography></FrameBox>}

      {sourceKey && loading && (
        <Box className="flex flex-col items-center justify-center gap-2" sx={{ minHeight: 220 }}>
          <DotLottleLoader />
          {mode === 'url' && (
            <Typography sx={DEMO_SX.mutedText}>
              Fetching the page and matching it to a category…
            </Typography>
          )}
        </Box>
      )}

      {sourceKey && !loading && error && (
        <FrameBox>
          <Typography sx={DEMO_SX.centeredMessage}>{error}</Typography>
          <Button onClick={() => refetch()} className="!px-4">
            <Typography>Retry</Typography>
          </Button>
        </FrameBox>
      )}

      {sourceKey && !loading && !error && (
        <>
          {product && <ProductCard product={product} />}

          {rows.length === 0 && (
            <FrameBox>
              <Typography sx={DEMO_SX.centeredMessage}>
                {product?.live
                  ? 'No expert scores above zero for these categories.'
                  : 'No scored experts for this product yet, try another product, or run a recompute on the Scores tab.'}
              </Typography>
            </FrameBox>
          )}

          {rows.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Hero row={rows[0]} bands={affinityBands} />
              {rows.length > 1 && (
                <>
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', mt: 2.5, mb: 0.5 }}>
                    Next best matches
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: 'var(--muted)', mb: 0.5 }}>
                    Click a row for its attribute overlap.
                  </Typography>
                  {rows.slice(1).map((row) => (
                    <RankedRow key={row.user_id} row={row} bands={affinityBands} />
                  ))}
                </>
              )}
            </Box>
          )}
        </>
      )}
    </Paper>
  );
};
